import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import {
  Alert,
  Chip,
  Stack,
  TextField,
} from '@mui/material';

import { useMaxSettings, useUpdateMaxSettings } from '../../hooks';
import { useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type { MaxSettings, UpdateMaxSettingsRequest } from '../../model/settingsTypes';
import {
  FeedbackSnackbar,
  FieldRow,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TextInputField,
} from '../components';

function toUpdateRequest(draft: MaxSettings): UpdateMaxSettingsRequest {
  const { botSecretConfigured, updatedAt, updatedBy, ...rest } = draft;
  return rest;
}

export function MaxSettingsPage() {
  const { canView, canManage, canManageCredentials } = useDomainPermissions('max');
  const query = useMaxSettings();
  const update = useUpdateMaxSettings();
  const form = useSettingsForm(query.data);
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toUpdateRequest(form.draft), {
      onSuccess: () => success('MAX settings saved.'),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title="MAX settings"
      subtitle="Channel behaviour, Mini App, support, bot identity and credentials."
      icon={<ChatRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        const credentialDisabled = disabled || !canManageCredentials;

        return (
          <Stack spacing={2.5}>
            {!canManageCredentials ? (
              <Alert severity="info">
                You can edit channel behaviour, but bot token and webhook secret require the credentials
                permission.
              </Alert>
            ) : null}

            <SettingsCard title="Channel behavior" icon={<TuneRoundedIcon />}>
              <SwitchField
                label="MAX enabled"
                description="Turn the MAX channel on or off for this tenant."
                checked={draft.maxEnabled}
                onChange={(value) => form.setField('maxEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Mini App enabled"
                description="Allow customers to open the storefront in the MAX mini app."
                checked={draft.miniAppEnabled}
                onChange={(value) => form.setField('miniAppEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Support enabled"
                description="Expose support/contact entry points in MAX."
                checked={draft.supportEnabled}
                onChange={(value) => form.setField('supportEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Phone collection"
                description="Ask customers for a phone number where it is needed."
                checked={draft.phoneCollectionEnabled}
                onChange={(value) => form.setField('phoneCollectionEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="FAQ enabled"
                description="Show FAQ/help content inside the channel."
                checked={draft.faqEnabled}
                onChange={(value) => form.setField('faqEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Support AI agent"
                description="Route support requests through the AI assistant."
                checked={draft.supportAiAgentEnabled}
                onChange={(value) => form.setField('supportAiAgentEnabled', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard title="Storefront identity" icon={<SmartToyRoundedIcon />}>
              <TextInputField
                label="Mini App URL"
                description="Public mini app address opened from MAX."
                value={draft.miniAppUrl ?? ''}
                onChange={(value) => form.setField('miniAppUrl', value)}
                disabled={disabled}
                type="url"
              />
              <TextInputField
                label="Support contact"
                description="Phone, username or contact card shown to customers."
                value={draft.supportContact ?? ''}
                onChange={(value) => form.setField('supportContact', value)}
                disabled={disabled}
              />
              <TextInputField
                label="Bot code"
                description="Stable internal bot identifier used for routing and secret storage."
                value={draft.botCode ?? ''}
                onChange={(value) => form.setField('botCode', value)}
                disabled={disabled}
              />
              <TextInputField
                label="Bot username"
                description="Public MAX bot username without the @ prefix."
                value={draft.botUsername ?? ''}
                onChange={(value) => form.setField('botUsername', value)}
                disabled={disabled}
              />
              <TextInputField
                label="Bot name"
                description="Display name shown in chat clients."
                value={draft.botName ?? ''}
                onChange={(value) => form.setField('botName', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Bot enabled"
                description="Enable the bot endpoint and channel routing."
                checked={draft.botEnabled}
                onChange={(value) => form.setField('botEnabled', value)}
                disabled={disabled}
              />
              <FieldRow
                label="Credentials status"
                description="Secret values are write-only and never returned to the browser."
              >
                <Chip
                  size="small"
                  variant="outlined"
                  color={draft.botSecretConfigured ? 'success' : 'default'}
                  label={draft.botSecretConfigured ? 'Configured' : 'Not configured'}
                />
              </FieldRow>
            </SettingsCard>

            <SettingsCard title="Secrets" icon={<SecurityRoundedIcon />}>
              <FieldRow
                label="Bot token"
                description="Leave blank to keep the current token."
                alignTop
              >
                <TextField
                  size="small"
                  fullWidth
                  type="password"
                  value={draft.botToken ?? ''}
                  disabled={credentialDisabled}
                  onChange={(event) => form.setField('botToken', event.target.value)}
                  autoComplete="new-password"
                />
              </FieldRow>
              <FieldRow
                label="Webhook secret"
                description="Leave blank to keep the current webhook secret."
                alignTop
              >
                <TextField
                  size="small"
                  fullWidth
                  type="password"
                  value={draft.webhookSecret ?? ''}
                  disabled={credentialDisabled}
                  onChange={(event) => form.setField('webhookSecret', event.target.value)}
                  autoComplete="new-password"
                />
              </FieldRow>
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
